import { Button, Checkbox, Flex, Select, Tooltip, Typography } from "antd";
import { OPTIONS_ORDER, RESERVATION_OPTIONS } from "config/consts";
import { useApp } from "contexts/AppProvider";
import { LanguageContext } from "contexts/LangProvider";
import { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { InfoCircleOutlined } from "@ant-design/icons";
import { ServiceHint } from "components/ServiceHint";
import { getDefaultVisitTypeOption } from "lib/reservationOptions";
import { Summary } from "./Summary";
import { ISingleOption } from "types";

export const REQUIRED_FIELD_REMINDER_DELAY_MS = 3000;

export const StartContent = ({
  activeCity,
  handleBranchChange,
  handleOnlineChange = (arg: boolean) => {},
  handleServiceChange,
  handleOptionChange,
  activeService,
  cities,
  services,
  isChild,
  setBirthYear,
  birthYear,
  availability,
  therapists,
  personalMode = false,
  locationSelectionDisabled = false,
}) => {
  const { setReservationPayload, dispatchReservationPayload, reservationPayload, isOnlineOnly, setActiveTimeslot } =
    useApp();
  const { formatMessage } = useIntl();
  const [initialTherapistChange, setInitialTherapistChange] = useState(true);
  const { locale } = useContext(LanguageContext);
  const [isHidden, setIsHidden] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [activeOptions, setActiveOptions] = useState<ISingleOption[]>([]);
  const [showRequiredWarnings, setShowRequiredWarnings] = useState(false);
  const [requiredReminderRevision, setRequiredReminderRevision] = useState(0);
  const requiredReminderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasMissingRequiredFieldsRef = useRef(false);
  const requiredMessageId = useId();

  const isTestGroup = true;

  useEffect(() => {
    if (activeService) {
      const options = activeService.options.filter(
        (el) => el.category === RESERVATION_OPTIONS.VISIT_TYPE && el.publicVisible !== false,
      );
      setActiveOptions(options);

      const persistedOption = options.find((option) => option.id === reservationPayload.visitTypeId);
      const defaultOption = getDefaultVisitTypeOption(activeService);

      if (!persistedOption && defaultOption) {
        handleOptionChange(defaultOption.id);
      }
    }
  }, [activeService, reservationPayload.visitTypeId]);
  useEffect(() => {
    if (
      !personalMode &&
      (!isTestGroup || !initialTherapistChange) &&
      activeService &&
      reservationPayload.visitTypeId &&
      ((isChild && birthYear) || !isChild) &&
      (activeCity || reservationPayload.isOnline)
    ) {
      setIsHidden(true);
    }
  }, [reservationPayload, activeCity, birthYear, isChild, activeService, initialTherapistChange, personalMode]);

  useEffect(() => {
    if (initialTherapistChange && reservationPayload.therapist && reservationPayload.therapist.length > 0) {
      setInitialTherapistChange(false);
    }
  }, [reservationPayload.therapist]);

  const languageOptions =
    activeService?.options.filter(
      (el) => el.category === RESERVATION_OPTIONS.LANGUGAGE && el.publicVisible !== false,
    ) ?? [];
  const supportsOnline = activeService?.options.some(
    (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
  );
  const isLocationMissing = !activeCity && !reservationPayload.isOnline;
  const isServiceMissing = !reservationPayload.serviceId;
  const isVisitTypeMissing = !reservationPayload.visitTypeId;
  const isBirthYearMissing = isChild && !birthYear;
  const hasMissingRequiredFields = isLocationMissing || isServiceMissing || isVisitTypeMissing || isBirthYearMissing;
  hasMissingRequiredFieldsRef.current = hasMissingRequiredFields;
  const locationWarning = showRequiredWarnings && isLocationMissing;
  const serviceWarning = showRequiredWarnings && isServiceMissing;
  const visitTypeWarning = showRequiredWarnings && isVisitTypeMissing;
  const birthYearWarning = showRequiredWarnings && isBirthYearMissing;
  const requiredFieldMessage = formatMessage({ defaultMessage: "To pole jest wymagane", id: "uaZA/F" });

  const clearRequiredReminder = useCallback(() => {
    if (requiredReminderTimeoutRef.current) {
      clearTimeout(requiredReminderTimeoutRef.current);
      requiredReminderTimeoutRef.current = null;
    }
  }, []);

  const scheduleRequiredReminder = useCallback(() => {
    setShowRequiredWarnings(false);
    setRequiredReminderRevision((revision) => revision + 1);
  }, []);

  useEffect(() => () => clearRequiredReminder(), [clearRequiredReminder]);

  useEffect(() => {
    if (requiredReminderRevision === 0) return;

    clearRequiredReminder();
    if (!hasMissingRequiredFieldsRef.current) return;

    requiredReminderTimeoutRef.current = setTimeout(() => {
      if (hasMissingRequiredFieldsRef.current) setShowRequiredWarnings(true);
      requiredReminderTimeoutRef.current = null;
    }, REQUIRED_FIELD_REMINDER_DELAY_MS);
  }, [requiredReminderRevision, clearRequiredReminder]);

  useEffect(() => {
    if (!hasMissingRequiredFields) {
      clearRequiredReminder();
      setShowRequiredWarnings(false);
    }
  }, [hasMissingRequiredFields, clearRequiredReminder]);

  return (
    <div
      className={personalMode ? "start-content start-content--personal" : "start-content"}
      style={{ width: "100%", margin: "0 auto", background: "#f6f6f6", borderRadius: "10px", padding: "10px 20px" }}
    >
      {isHidden ? (
        <Summary activeCity={activeCity} activeService={activeService} setIsHidden={setIsHidden} />
      ) : (
        <>
          <>
            {!personalMode && (
              <Flex style={{ marginBottom: 15 }}>
                <Button
                  style={{ marginRight: 10 }}
                  type={reservationPayload.isOnline ? "default" : "primary"}
                  onClick={() => {
                    handleBranchChange(null);
                    scheduleRequiredReminder();
                  }}
                  disabled={isOnlineOnly}
                >
                  {formatMessage({ defaultMessage: "Wizyta stacjonarna", id: "TatblS" })}
                </Button>
                <Button
                  onClick={() => {
                    handleBranchChange("online");
                    scheduleRequiredReminder();
                  }}
                  type={reservationPayload.isOnline ? "primary" : "default"}
                >
                  {formatMessage({ defaultMessage: "Wizyta online", id: "iysARY" })}
                </Button>
              </Flex>
            )}
            <div className={personalMode ? "personal-location-field" : undefined} style={{ marginBottom: 15 }}>
              <Flex vertical className={personalMode ? "personal-location-field__branches" : undefined}>
                <Typography.Text strong style={{ marginBottom: 5 }}>
                  {personalMode
                    ? formatMessage({ defaultMessage: "Lokalizacja", id: "VAnOby" })
                    : formatMessage({ defaultMessage: "Miasto", id: "STzyfo" })}
                </Typography.Text>
                <Select
                  disabled={isOnlineOnly || locationSelectionDisabled || (personalMode && cities.length === 1)}
                  showSearch
                  placeholder={formatMessage({
                    defaultMessage: "Wybierz placówkę",
                    id: "M2NSwf",
                  })}
                  style={{ width: "100%" }}
                  value={locationSelectionDisabled ? "any" : isOnlineOnly ? "online" : activeCity}
                  status={locationWarning ? "warning" : undefined}
                  aria-required="true"
                  aria-invalid={locationWarning || undefined}
                  aria-describedby={locationWarning ? `${requiredMessageId}-location` : undefined}
                  onChange={(value) => {
                    handleBranchChange(value);
                    scheduleRequiredReminder();
                  }}
                  options={
                    [
                      ...(personalMode
                        ? [
                            {
                              label: formatMessage({ defaultMessage: "Dowolna lokalizacja", id: "+XlYpU" }),
                              value: "any",
                            },
                          ]
                        : [
                            {
                              label: "Online",
                              value: "online",
                            },
                          ]),
                      ...cities
                        .slice()
                        .sort((a: string, b: string) => a.localeCompare(b))
                        .map((el: string) => ({
                          label: el,
                          value: el,
                        })),
                    ] as { label: string; value: string }[]
                  }
                  onDropdownVisibleChange={(open) => {
                    if (!open) {
                      (document.activeElement as HTMLElement)?.blur();
                    }
                  }}
                />
                {locationWarning && (
                  <Typography.Text
                    id={`${requiredMessageId}-location`}
                    className="required-field-message"
                    role="status"
                    aria-live="polite"
                  >
                    {requiredFieldMessage}
                  </Typography.Text>
                )}
              </Flex>
              {personalMode && supportsOnline && (
                <Checkbox
                  className="personal-location-field__online"
                  checked={reservationPayload.isOnline}
                  onChange={(event) => {
                    handleOnlineChange(event.target.checked);
                    scheduleRequiredReminder();
                  }}
                >
                  {formatMessage({ defaultMessage: "Wizyta online", id: "iysARY" })}
                </Checkbox>
              )}
            </div>

            <Flex vertical className={personalMode ? "personal-compact-field" : undefined} style={{ marginBottom: 15 }}>
              <Typography.Text strong style={{ marginBottom: 5 }}>
                {formatMessage({ defaultMessage: "Typ wizyty", id: "fZQn0m" })}
                <InfoCircleOutlined style={{ marginLeft: "10px" }} onClick={() => setShowHelp(true)} />
              </Typography.Text>
              <div className="required-field-control">
                <Select
                  placeholder={formatMessage({
                    defaultMessage: "Wybierz typ wizyty",
                    id: "7QQaY4",
                  })}
                  style={{ width: "100%" }}
                  value={reservationPayload.serviceId.length > 0 ? reservationPayload.serviceId : undefined}
                  status={serviceWarning ? "warning" : undefined}
                  aria-required="true"
                  aria-invalid={serviceWarning || undefined}
                  aria-describedby={serviceWarning ? `${requiredMessageId}-service` : undefined}
                  onChange={(value) => {
                    handleServiceChange(value);
                    scheduleRequiredReminder();
                  }}
                  options={services
                    .filter((el) => el.publicVisible !== false)
                    .map((el) => ({
                      label: el.translations[locale],
                      value: el.id,
                    }))}
                />
                {serviceWarning && (
                  <Typography.Text
                    id={`${requiredMessageId}-service`}
                    className="required-field-message"
                    role="status"
                    aria-live="polite"
                  >
                    {requiredFieldMessage}
                  </Typography.Text>
                )}
              </div>
            </Flex>
            <Flex vertical className={personalMode ? "personal-compact-field" : undefined} style={{ marginBottom: 15 }}>
              <Typography.Text strong style={{ marginBottom: 5 }}>
                {formatMessage({ defaultMessage: "Dla kogo", id: "TLHScf" })}
              </Typography.Text>
              <div className="required-field-control">
                <Select
                  placeholder={formatMessage({
                    defaultMessage: "Wybierz dla kogo wizyta",
                    id: "/5ybgV",
                  })}
                  style={{ width: "100%" }}
                  value={reservationPayload.visitTypeId}
                  status={visitTypeWarning ? "warning" : undefined}
                  aria-required="true"
                  aria-invalid={visitTypeWarning || undefined}
                  aria-describedby={visitTypeWarning ? `${requiredMessageId}-visit-type` : undefined}
                  onChange={(value) => {
                    handleOptionChange(value);
                    scheduleRequiredReminder();
                  }}
                  disabled={!activeService}
                  options={activeOptions
                    .sort((a, b) => OPTIONS_ORDER.indexOf(a.name) - OPTIONS_ORDER.indexOf(b.name))
                    .map((el) => ({
                      label: el.translations[locale],
                      value: el.id,
                    }))}
                />
                {visitTypeWarning && (
                  <Typography.Text
                    id={`${requiredMessageId}-visit-type`}
                    className="required-field-message"
                    role="status"
                    aria-live="polite"
                  >
                    {requiredFieldMessage}
                  </Typography.Text>
                )}
              </div>
            </Flex>
            {isChild && (
              <div style={{ marginBottom: 15 }}>
                <Flex
                  vertical
                  className={personalMode ? "personal-compact-field" : undefined}
                  style={{ marginBottom: 0 }}
                >
                  <Typography.Text strong style={{ marginBottom: 5 }}>
                    {formatMessage({ defaultMessage: "Rok urodzenia dziecka", id: "iRDB9+" })}
                    <Typography.Text type="danger" aria-hidden="true">
                      {" *"}
                    </Typography.Text>
                  </Typography.Text>
                  <div className="required-field-control">
                    <Select
                      value={birthYear}
                      placeholder={formatMessage({ defaultMessage: "Wybierz rok urodzenia", id: "mNkTsO" })}
                      style={{ width: "100%" }}
                      status={birthYearWarning ? "warning" : undefined}
                      aria-required="true"
                      aria-invalid={birthYearWarning || undefined}
                      aria-describedby={`${requiredMessageId}-birth-year`}
                      onChange={(value) => {
                        setBirthYear(value);
                        scheduleRequiredReminder();
                      }}
                    >
                      {Array.from({ length: 19 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <Select.Option key={year} value={year}>
                          {year}
                        </Select.Option>
                      ))}
                    </Select>
                    <Typography.Text
                      id={`${requiredMessageId}-birth-year`}
                      className={birthYearWarning ? "required-field-message" : "required-field-note"}
                      role={birthYearWarning ? "status" : undefined}
                      aria-live="polite"
                    >
                      {formatMessage({
                        defaultMessage: "Podanie roku urodzenia dziecka jest konieczne, aby wyszukać dostępne terminy.",
                        id: "7f6qkF",
                      })}
                    </Typography.Text>
                  </div>
                </Flex>
              </div>
            )}

            {languageOptions.length > 1 && activeService && (
              <Flex vertical style={{ marginBottom: 15 }}>
                <Typography.Text strong style={{ marginBottom: 5 }}>
                  {formatMessage({ defaultMessage: "Język", id: "p3WNo/" })}
                </Typography.Text>
                <Select
                  value={languageOptions.find((option) => reservationPayload.optionIds.includes(option.id))?.id ?? "pl"}
                  onChange={(optionId) => {
                    dispatchReservationPayload({
                      type: "selectLanguageOption",
                      optionId: optionId === "pl" ? undefined : optionId,
                      activeService,
                    });
                    scheduleRequiredReminder();
                  }}
                  options={[
                    {
                      label: formatMessage({ defaultMessage: "polski", id: "kplhcv" }),
                      value: "pl",
                    },
                    ...languageOptions.map((languageOption) => ({
                      label: languageOption.translations[locale],
                      value: languageOption.id,
                    })),
                  ]}
                />
              </Flex>
            )}
            {languageOptions.length === 1 && (
              <Flex vertical style={{ marginBottom: 15 }}>
                {languageOptions.map((languageOption) => (
                  <Checkbox
                    key={languageOption.id}
                    checked={reservationPayload.optionIds.includes(languageOption.id)}
                    onChange={(e) => {
                      dispatchReservationPayload({
                        type: "setLanguageOption",
                        optionId: languageOption.id,
                        enabled: e.target.checked,
                      });
                      scheduleRequiredReminder();
                    }}
                  >
                    {languageOption.translations[locale]}
                  </Checkbox>
                ))}
              </Flex>
            )}
          </>
          <>
            {!personalMode && availability && availability.length > 0 && reservationPayload.visitTypeId && (
              <div>
                <Flex vertical style={{ marginBottom: 15 }}>
                  <Typography.Text strong>
                    {formatMessage({ defaultMessage: "Specjalista", id: "dz8aKC" })}
                  </Typography.Text>
                  <Select
                    placeholder={formatMessage({ defaultMessage: "Dowolny specjalista", id: "PpkeBx" })}
                    showSearch
                    style={{ width: "100%" }}
                    value={reservationPayload.therapist}
                    onChange={(value) => {
                      setReservationPayload({ ...reservationPayload, therapist: value });
                      setActiveTimeslot(null);
                      scheduleRequiredReminder();
                    }}
                    options={[
                      ...therapists
                        .sort((a, b) => a.lastName.localeCompare(b.lastName))
                        .map((el) => ({
                          label: el.name,
                          value: el.name,
                        })),
                      { label: formatMessage({ defaultMessage: "Dowolny specjalista", id: "PpkeBx" }), value: "" },
                    ]}
                  />
                </Flex>
              </div>
            )}
          </>
        </>
      )}

      <ServiceHint locale={locale} setShowHelp={setShowHelp} showHelp={showHelp} />
    </div>
  );
};
